import { apiFetch } from './api/http'

// Mirrors app/api/segment_revenue_cache.py (MP-OS-163). Missing segment periods are
// computed once in the background; the API answers 202 with Retry-After (POLL_AFTER)
// until the WHOLE range is verified. A computation is bounded by JOB_TIMEOUT and a
// crashed worker's claim expires after LEASE (JOB_TIMEOUT + 30 s).
export const SEGMENT_POLL_AFTER_S = 5
export const SEGMENT_JOB_TIMEOUT_S = 240
const SEGMENT_LEASE_S = SEGMENT_JOB_TIMEOUT_S + 30
// One lease plus a poll and request margin: past this the computation is stuck, not slow.
export const SEGMENT_POLL_BOUND_MS = (SEGMENT_LEASE_S + 30) * 1000
// Production: a cold ten-month range took 77-90+ s. Past this, say it is still working.
export const SEGMENT_SLOW_AFTER_MS = 90_000
const MAX_POLL_S = 30
// The API holds a failed computation for FAILURE_HOLD (30 s); never ask a user to wait longer than this.
const MAX_HOLD_S = 300
export const SEGMENT_TIMEOUT_MESSAGE = 'Revenue by segment is taking longer than expected. Please retry.'

export type BusinessProgress = 'calculating' | 'slow'

/** A failed answer. ``retryAfterS``: the server's Retry-After (a held failure), if any. */
export class BusinessMetricsError extends Error {
  constructor(message: string, readonly retryAfterS?: number) { super(message) }
}

function retryAfterSeconds(r: Response): number | undefined {
  const seconds = Number(r.headers?.get('Retry-After') ?? NaN)
  return Number.isFinite(seconds) && seconds > 0 ? Math.min(MAX_HOLD_S, Math.ceil(seconds)) : undefined
}

/** One business-metrics answer. 202 is progress, never a partial result. */
export async function fetchBusinessMetrics<T>(url: string, signal: AbortSignal,
  onProgress?: (progress: BusinessProgress) => void): Promise<T> {
  const started = Date.now()
  for (;;) {
    signal.throwIfAborted()
    const r = await apiFetch(url, { signal })
    signal.throwIfAborted()
    if (r.status === 202) {
      const elapsed = Date.now() - started
      const remaining = SEGMENT_POLL_BOUND_MS - elapsed
      if (remaining <= 0) throw new Error(SEGMENT_TIMEOUT_MESSAGE)
      onProgress?.(elapsed >= SEGMENT_SLOW_AFTER_MS ? 'slow' : 'calculating')
      const seconds = Number(r.headers?.get('Retry-After') ?? SEGMENT_POLL_AFTER_S)
      const wait = Number.isFinite(seconds) ? Math.min(MAX_POLL_S, Math.max(1, seconds)) : SEGMENT_POLL_AFTER_S
      await new Promise<void>((resolve, reject) => {
        const abort = () => { clearTimeout(timer); reject(signal.reason) }
        const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve() },
          Math.min(wait * 1000, remaining))
        signal.addEventListener('abort', abort, { once: true })
      })
      continue
    }
    const body = await r.json()
    if (!r.ok) throw new BusinessMetricsError(body.detail || 'Business data could not be loaded.', retryAfterSeconds(r))
    return body as T
  }
}
